import { useEffect, useRef, useState } from "react";
import { X, ArrowUp } from "lucide-react";
import { useChatSafeArea } from "@/lib/useChatSafeArea";
import { safeStorage } from "@/lib/safeStorage";

const API = process.env.REACT_APP_BACKEND_URL;

let fallbackSessionId;
const getSessionId = () => {
  let sid = safeStorage.get("dcyb_chat_session");
  if (!sid) {
    sid = fallbackSessionId || crypto.randomUUID();
    fallbackSessionId = sid;
    safeStorage.set("dcyb_chat_session", sid);
  }
  return sid;
};

const WELCOME = {
  role: "assistant",
  content:
    "Welcome to DCYB. I can walk you through the mission, Certified Partner tiers, events, or the deck library — and help you get a meeting with Luke. What brings you here?",
};

const STARTERS = [
  "What is the Anchor tier?",
  "How does DCYB incubation work?",
  "Where has DCYB been featured?",
  "How big is DCYB's media reach?",
];

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const insideSafeArea = useChatSafeArea();
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());
  useEffect(() => {
    const openChat = (event) => {
      setOpen(true);
      if (event?.detail?.prompt) setInput(event.detail.prompt);
    };
    window.addEventListener("dcyb:open-chat", openChat);
    return () => window.removeEventListener("dcyb:open-chat", openChat);
  }, []);

  useEffect(() => {
    if (!open || loaded) return;
    fetch(`${API}/api/chat/history/${sessionId.current}`)
      .then((r) => r.json())
      .then((rows) => {
        if (Array.isArray(rows) && rows.length > 0) {
          setMessages([WELCOME, ...rows.map((r) => ({ role: r.role, content: r.content }))]);
        }
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [open, loaded]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (preset) => {
    const text = (typeof preset === "string" ? preset : input).trim();
    if (!text || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((prev) => [...prev, { role: "user", content: text }, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop();
        for (const evt of events) {
          if (!evt.startsWith("data: ")) continue;
          const data = JSON.parse(evt.slice(6));
          if (data.delta) {
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = {
                role: "assistant",
                content: next[next.length - 1].content + data.delta,
              };
              return next;
            });
          }
          if (data.error) {
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { role: "assistant", content: data.error };
              return next;
            });
          }
        }
      }
    } catch {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          role: "assistant",
          content: "The concierge hit a snag. Please try again.",
        };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-24 right-4 sm:right-6 z-30 w-[calc(100vw-2rem)] sm:w-[380px] h-[560px] max-h-[calc(100vh-8rem)] flex flex-col bg-white border border-[#D2D5DA] shadow-[0_24px_64px_rgba(15,17,23,0.18)]"
        >
          <div className="flex items-center justify-between px-4 py-3.5 bg-[#0F1117] border-b border-[#1A1D26]">
            <div className="flex items-center gap-2.5">
              <img src="/robot-avatar.png" alt="DCYB robot" className="w-6 h-6 object-contain" />
              <span className="font-mono-label text-[11px] tracking-[0.22em] uppercase text-white">
                DCYB BOT
              </span>
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full bg-red-500 opacity-60" />
                <span className="relative inline-flex h-2 w-2 bg-red-500" />
              </span>
            </div>
            <button
              data-testid="chat-close-button"
              onClick={() => setOpen(false)}
              className="text-[#8A8D94] hover:text-white transition-colors"
              aria-label="Close chat"
            >
              <X size={16} />
            </button>
          </div>

          <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#F4F4F6]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={m.role === "user" ? "chat-user-message" : "chat-assistant-message"}
                  className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#0F1117] text-white"
                      : "bg-white border border-[#D2D5DA] text-[#11131A]"
                  }`}
                >
                  {m.content || (
                    <span className="inline-flex gap-1 items-center py-1">
                      <span className="w-1.5 h-1.5 bg-[#5F6168] animate-pulse" />
                      <span className="w-1.5 h-1.5 bg-[#5F6168] animate-pulse [animation-delay:150ms]" />
                      <span className="w-1.5 h-1.5 bg-[#5F6168] animate-pulse [animation-delay:300ms]" />
                    </span>
                  )}
                </div>
              </div>
            ))}
            {!streaming && !messages.some((m) => m.role === "user") && (
              <div data-testid="chat-starter-questions" className="flex flex-wrap gap-2 pt-1">
                {STARTERS.map((s, i) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`chat-starter-${i}`}
                    onClick={() => send(s)}
                    className="bg-white border border-[#D2D5DA] hover:border-[#0F1117] px-3 py-2 text-[12px] text-[#3D4049] hover:text-[#0F1117] text-left transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 px-3 py-3 bg-white border-t border-[#D2D5DA]">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about DCYB…"
              className="flex-1 bg-[#F4F4F6] border border-[#D2D5DA] px-3 py-2.5 text-sm text-[#11131A] placeholder:text-[#8A8D94] outline-none focus:border-[#0F1117] transition-colors"
            />
            <button
              data-testid="chat-send-button"
              onClick={send}
              disabled={streaming || !input.trim()}
              className="chrome-btn-dark inline-flex items-center justify-center w-10 h-10 disabled:opacity-40"
              aria-label="Send message"
            >
              <ArrowUp size={15} />
            </button>
          </div>
        </div>
      )}

      {!open ? !insideSafeArea && (
        <button
          data-testid="chat-bubble-button"
          onClick={() => setOpen(true)}
          className="bot-greeting fixed bottom-4 right-4 sm:right-6 z-30 flex flex-col items-end gap-2"
          aria-label="Open DCYB BOT chat"
        >
          <div data-testid="chat-greeting-message" className="hidden sm:block bg-white border border-[#D2D5DA] shadow-[0_12px_32px_rgba(15,17,23,0.14)] px-3 py-2 sm:px-3.5 sm:py-2.5 text-xs sm:text-sm text-[#11131A]">
            Ask me anything.
          </div>
          <img
            data-testid="chat-launcher-avatar"
            src="/robot-wave.png"
            alt="DCYB BOT waving"
            className="w-14 sm:w-20 h-auto drop-shadow-[0_10px_20px_rgba(15,17,23,0.25)] mr-2"
          />
        </button>
      ) : (
        <button
          data-testid="chat-bubble-button"
          onClick={() => setOpen(false)}
          className="chrome-btn-dark fixed bottom-6 right-4 sm:right-6 z-30 w-14 h-14 inline-flex items-center justify-center"
          aria-label="Close DCYB BOT chat"
        >
          <X size={20} />
        </button>
      )}
    </>
  );
};

export default ChatWidget;
